import React, { useEffect, useState } from 'react';
import { createManualRegistration } from '../../services/api';
import { calculateFeeBreakdown } from '../../utils/registrationFee';

const feeTypes = [
  { key: 'standard', label: 'Standard' },
  { key: 'student', label: 'Student' }
];

export default function ManualAttendeeRegistrationModal({ conference, onClose, onSuccess, show }) {
  const [step, setStep] = useState(1); // 1 = configuration, 2 = confirmation
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [feeType, setFeeType] = useState('standard');
  const [withAccompanied, setWithAccompanied] = useState(false);
  const [accompaniedPersonName, setAccompaniedPersonName] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('EUR');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [amountEdited, setAmountEdited] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const fees = conference?.fees || {};
  const baseCurrency = fees.currency || 'EUR';
  const currencyOptions = Array.from(new Set([baseCurrency, 'EUR', 'USD']));
  const { total: calculatedAmount, currency: displayCurrency } = calculateFeeBreakdown({
    feeType,
    fees,
    accompaniedPersonsCount: withAccompanied ? 1 : 0,
    paymentCurrency
  });

  useEffect(() => {
    if (!amountEdited) {
      setPaymentAmount(String(calculatedAmount));
    }
  }, [calculatedAmount, amountEdited]);

  useEffect(() => {
    if (!withAccompanied) setAccompaniedPersonName('');
  }, [withAccompanied]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setAttendeeEmail('');
    setFeeType('standard');
    setWithAccompanied(false);
    setPaymentCurrency(baseCurrency);
    setPaymentAmount('');
    setAmountEdited(false);
    setAccompaniedPersonName('');
    setNotes('');
    setError('');
    setSuccessMessage('');
  }, [show, baseCurrency]);

  const handleSubmit = async () => {
    if (!conference) return;
    if (!attendeeEmail) {
      setError('Please enter an attendee email.');
      return;
    }

    if (withAccompanied && !accompaniedPersonName.trim()) {
      setError('Please enter the accompanied person name.');
      return;
    }

    const parsedAmount = Number(paymentAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Please enter a valid total amount.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await createManualRegistration({
        conferenceId: conference._id,
        attendeeEmail,
        feeType,
        accompaniedPersonsCount: withAccompanied ? 1 : 0,
        accompaniedPersonName: withAccompanied ? accompaniedPersonName.trim() : undefined,
        paymentCurrency,
        paymentAmount: parsedAmount,
        notes: notes || undefined
      });
      setSuccessMessage('Manual attendee registration completed.');
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create manual registration.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !conference) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Manual Attendee Registration {step === 2 && '- Confirmation'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading}></button>
          </div>
          <div className="modal-body">
            <p><strong>Conference:</strong> {conference.title}</p>

            {step === 1 ? (
              // Step 1: Configuration
              <>
                <div className="mb-3">
                  <label className="form-label">Attendee Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={attendeeEmail}
                    onChange={(e) => setAttendeeEmail(e.target.value)}
                    placeholder="user@example.com"
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Currency</label>
                  <select
                    className="form-select"
                    value={paymentCurrency}
                    onChange={(e) => setPaymentCurrency(e.target.value)}
                    disabled={loading}
                  >
                    {currencyOptions.map((cur) => (
                      <option key={cur} value={cur}>{cur}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Select Fee Type</label>
                  <select className="form-select" value={feeType} onChange={(e) => setFeeType(e.target.value)} disabled={loading}>
                    {feeTypes.map((f) => {
                      const { base } = calculateFeeBreakdown({ feeType: f.key, fees, accompaniedPersonsCount: 0, paymentCurrency });
                      return (
                        <option key={f.key} value={f.key}>{f.label} ({displayCurrency} {base})</option>
                      );
                    })}
                  </select>
                </div>

                <div className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      id="attendeeWithAccompanied"
                      className="form-check-input"
                      checked={withAccompanied}
                      onChange={(e) => setWithAccompanied(e.target.checked)}
                      disabled={loading}
                    />
                    <label htmlFor="attendeeWithAccompanied" className="form-check-label">
                      Include Accompanied Person {`(+${displayCurrency} ${calculateFeeBreakdown({ feeType, fees, accompaniedPersonsCount: 1, paymentCurrency }).accompaniedFee})`}
                    </label>
                  </div>
                  {withAccompanied && (
                    <div className="mt-2">
                      <label className="form-label">Accompanied Person Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={accompaniedPersonName}
                        onChange={(e) => setAccompaniedPersonName(e.target.value)}
                        placeholder="Full name"
                        disabled={loading}
                      />
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Total Amount</label>
                  <div className="input-group">
                    <span className="input-group-text">{displayCurrency}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={paymentAmount}
                      onChange={(e) => {
                        setAmountEdited(true);
                        setPaymentAmount(e.target.value);
                      }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setAmountEdited(false);
                        setPaymentAmount(String(calculatedAmount));
                      }}
                      disabled={loading}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Comments</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any internal notes for this manual registration..."
                    disabled={loading}
                  />
                </div>
                
                <div className="alert alert-info">
                  <strong>Total Amount:</strong> {displayCurrency} {paymentAmount || calculatedAmount}
                </div>
              </>
            ) : (
              // Step 2: Confirmation
              <>
                <div className="mb-3">
                  <p><strong>Attendee Email:</strong> {attendeeEmail}</p>
                  <p><strong>Fee Type:</strong> {feeType === 'student' ? 'Student' : 'Standard'}</p>
                  {withAccompanied && <p><strong>Includes:</strong> Accompanied Person</p>}
                  {withAccompanied && accompaniedPersonName.trim() && (
                    <p><strong>Accompanied Person:</strong> {accompaniedPersonName}</p>
                  )}
                  <p><strong>Currency:</strong> {displayCurrency}</p>
                  {notes && (
                    <>
                      <p><strong>Notes:</strong></p>
                      <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{notes}</p>
                    </>
                  )}
                  <p className="h5"><strong>Total:</strong> {displayCurrency} {paymentAmount || calculatedAmount}</p>
                </div>

                {error && (
                  <div className="alert alert-danger py-2">{error}</div>
                )}
                {successMessage && (
                  <div className="alert alert-success py-2">{successMessage}</div>
                )}
                {loading && <div className="small text-muted mb-2">Processing...</div>}
              </>
            )}
          </div>
          <div className="modal-footer">
            {step === 1 ? (
              <>
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setStep(2)}
                  disabled={!attendeeEmail || loading || (withAccompanied && !accompaniedPersonName.trim())}
                >
                  Review Registration
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => setStep(1)} disabled={loading}>
                  Back
                </button>
                <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  Confirm & Register
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
